import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { hero, audienceCards, about, homeServices, contactInfo } from "../mock/mock";
import Testimonials from "../components/Testimonials";
import QuoteForm from "../components/QuoteForm";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";

const Home = () => {
  const navigate = useNavigate();
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setOffsetY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="pt-16 sm:pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={hero.image}
            alt="Déménagement professionnel"
            className="w-full h-[120%] object-cover will-change-transform"
            style={{ transform: `translateY(${offsetY * 0.25}px)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#131f38]/95 via-[#1B2A4A]/85 to-[#1B2A4A]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28 lg:py-36">
          <div className="max-w-2xl animate-fadeUp">
            <span className="inline-block bg-[#C9A227] text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">
              Particuliers & Professionnels
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
              {hero.title} <br />
              <span className="text-[#C9A227]">{hero.titleAccent}</span>
            </h1>
            <p className="mt-6 text-lg text-gray-200 leading-relaxed max-w-xl">
              {hero.subtitle}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200 shadow-lg"
              >
                {hero.ctaPrimary} <ArrowRight size={18} />
              </button>
              <a
                href="#services"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur border border-white/30 text-white hover:bg-white/20 font-semibold px-7 py-3.5 rounded-lg transition-colors duration-200"
              >
                {hero.ctaSecondary}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE CARDS */}
      <section className="relative -mt-12 sm:-mt-16 z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {audienceCards.map((card, idx) => (
            <Reveal key={card.id} delay={idx * 120} className="group relative overflow-hidden rounded-2xl shadow-xl bg-white border border-gray-100 hover:-translate-y-1 transition-all duration-300">
              <Link to={card.link} className="block">
                <div className="h-44 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#1B2A4A]">{card.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed">{card.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-[#C9A227] font-semibold text-sm group-hover:gap-2.5 transition-all">
                    En savoir plus <ArrowRight size={16} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Nos services</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-4">
              Une offre complète pour votre déménagement
            </h2>
            <p className="text-gray-600">
              De l'emballage au montage, nous prenons en charge chaque étape avec professionnalisme.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeServices.map((s, i) => {
              const Icon = Icons[s.icon] || Icons.Package;
              return (
                <Reveal
                  key={i}
                  delay={(i % 3) * 100}
                  className="group bg-white rounded-2xl p-7 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-[#C9A227]/40 transition-all duration-300"
                >
                  <div className="w-14 h-14 rounded-xl bg-[#1B2A4A]/5 group-hover:bg-[#1B2A4A] flex items-center justify-center transition-colors duration-300">
                    <Icon size={26} className="text-[#1B2A4A] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="mt-5 text-lg font-bold text-[#1B2A4A]">{s.title}</h3>
                  <p className="mt-2 text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 sm:py-20 bg-[#f7f9fc]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">À propos</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-6">{about.title}</h2>
            {about.paragraphs.map((p, i) => (
              <p key={i} className="text-gray-600 leading-relaxed mb-4">{p}</p>
            ))}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-8">
              {about.stats.map((stat, i) => (
                <div key={i} className="text-center bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                  <p className="text-2xl sm:text-3xl font-extrabold text-[#1B2A4A]">
                    <AnimatedCounter value={stat.value} />
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={150} className="relative">
            <img src={about.image} alt="Notre équipe" className="rounded-2xl shadow-xl w-full h-[320px] sm:h-[420px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-[#1B2A4A] text-white rounded-2xl p-6 shadow-xl hidden sm:block max-w-[220px]">
              <p className="font-bold text-lg">Assurance tous risques</p>
              <p className="text-sm text-gray-300 mt-1">Vos biens protégés du début à la fin.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Testimonials />

      {/* QUOTE CTA */}
      <section id="devis" className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <span className="text-[#C9A227] font-semibold text-sm uppercase tracking-wider">Devis</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B2A4A] mt-2 mb-4">{contactInfo.title}</h2>
            <p className="text-gray-600 leading-relaxed mb-8">{contactInfo.subtitle}</p>
            <ul className="space-y-4">
              {["Devis gratuit et sans engagement", "Réponse rapide sous 24h", "Accompagnement personnalisé", "Tarifs transparents sans frais cachés"].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-gray-700">
                  <CheckCircle2 size={20} className="text-[#C9A227] shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={150}>
            <QuoteForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
